import React, { useMemo, useState } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import AddGroupModal from "../components/AddGroupModal";
import { FaSearch, FaEdit, FaTrash, FaUsers, FaPlus } from "react-icons/fa";
import getApiUrl from "../utils/api";
import { usePostHog } from "posthog-js/react";
import "./Groups.css";

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [showEditor, setShowEditor] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const posthog = usePostHog();
  const [isGroupLoading, setIsGroupLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOpenCreate = () => {
    setEditingIndex(-1);
    setShowEditor(true);
  };

  // Initial load from backend, scoped to current user
  React.useEffect(() => {
    const load = async () => {
      try {
        const meRes = await fetch(getApiUrl("api/auth/me"), {
          credentials: "include",
        });
        if (!meRes.ok) {
          setGroups([]);
          return;
        }
        const meData = await meRes.json();
        const me = meData?.user;
        if (!me) {
          setGroups([]);
          return;
        }
        const myId = me.userId || me.id || me.sub;
        const myEmail = me.email || "";

        setIsGroupLoading(true);
        const gRes = await fetch(getApiUrl("api/group"), {
          credentials: "include",
        });
        const allGroups = await gRes.json();
        if (!Array.isArray(allGroups)) {
          setGroups([]);
          return;
        }

        const isMember = (g) => {
          if (Array.isArray(g.members)) {
            return g.members.some(
              (m) => String(m.id || m.user_id) === String(myId)
            );
          }
          if (Array.isArray(g.memberIds)) {
            return g.memberIds.some((id) => String(id) === String(myId));
          }
          if (Array.isArray(g.memberLabels)) {
            return g.memberLabels.some(
              (lbl) => typeof lbl === "string" && lbl.includes(myEmail)
            );
          }
          if (Array.isArray(g.member_labels)) {
            return g.member_labels.some(
              (lbl) => typeof lbl === "string" && lbl.includes(myEmail)
            );
          }
          return false;
        };

        const createdBy = (g) =>
          String(g.created_by || g.createdBy || g.owner_id || "") ===
          String(myId);

        const filtered = allGroups.filter((g) => createdBy(g) || isMember(g));
        setGroups(filtered);
      } catch {
        setGroups([]);
      }
      finally {
        setIsGroupLoading(false);
      }
    };
    load();
  }, []);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;

    const query = searchQuery.toLowerCase();
    return groups.filter(
      (group) =>
        (group.groupName || "").toLowerCase().includes(query) ||
        (group.description || "").toLowerCase().includes(query) ||
        (Array.isArray(group.memberLabels)
          ? group.memberLabels.join(" ").toLowerCase()
          : ""
        ).includes(query)
    );
  }, [groups, searchQuery]);

  const handleSubmitGroup = async (group) => {
    try {
      if (editingIndex >= 0) {
        const current = groups[editingIndex];
        const res = await fetch(getApiUrl(`api/group/${current.id}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(group),
        });

        if (res.ok) {
          posthog.capture('Group Updated', { name: group.groupName });
        }
      } else {
        const res = await fetch(getApiUrl("api/group"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(group),
        });

        if (res.ok) {
          posthog.capture('Group Created', { name: group.groupName });
        }
      }
      const refreshed = await fetch(getApiUrl("api/group"), {
        credentials: "include",
      }).then((r) => r.json());
      setGroups(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      /* ignore */
    } finally {
      setShowEditor(false);
      setEditingIndex(-1);
    }
  };

  const handleEdit = (idx) => {
    setEditingIndex(idx);
    setShowEditor(true);
  };

  const handleDelete = async (idx) => {
    const target = groups[idx];
    if (!target) return;

    setDeleteTarget({ target, idx });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    const { target, idx } = deleteTarget;
    try {
      const res = await fetch(getApiUrl(`api/group/${target.id}`), {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("Failed to delete group");
      }

      setGroups((prev) => prev.filter((_, i) => i !== idx));
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Error deleting group:", error);
      // Optionally add a toast or error message here
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <DashboardLayout modalOpen={showEditor}>
      <AddGroupModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSubmit={handleSubmitGroup}
        initialData={editingIndex >= 0 ? groups[editingIndex] : undefined}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              animation: "fadeInModal 0.3s ease-out",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                color: "#dc3545",
                fontSize: "1.2rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaTrash size={20} />
              Delete Group
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                color: "#666",
                fontSize: "0.95rem",
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to delete the group{" "}
              <strong>"{deleteTarget?.target?.groupName}"</strong>?
            </p>
            <p
              style={{
                margin: "0 0 20px 0",
                color: "#dc3545",
                fontSize: "0.85rem",
                fontWeight: 500,
                padding: "12px",
                background: "#fef2f2",
                borderRadius: "6px",
                border: "1px solid #fecaca",
              }}
            >
              ⚠️ This action cannot be undone. All group data will be
              permanently deleted.
            </p>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTarget(null);
                }}
                style={{
                  padding: "0.45rem 0.9rem",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  background: "#fff",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f5f5f5";
                  e.target.style.borderColor = "#ccc";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.borderColor = "#ddd";
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "0.45rem 0.9rem",
                  border: "none",
                  borderRadius: "5px",
                  background: "#dc3545",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <div className="table-loader" style={{ width: "14px", height: "14px", borderWidth: "2px" }} />
                    Deleting...
                  </div>
                ) : (
                  <>
                    <FaTrash size={12} />
                    Delete Group
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )
      }
      <div className="groups-container">
        {/* Header Section */}
        <div className="groups-header">
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "1.75rem",
                fontWeight: 700,
                color: "#1a1a1a",
                marginBottom: "0.5rem",
              }}
            >
              Groups Management
            </h2>
            <div
              style={{
                color: "#666",
                fontSize: "0.95rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaUsers size={16} />
              Manage your teams and group assignments
            </div>
          </div>
          <button
            onClick={handleOpenCreate}
            className="groups-create-btn"
            style={{
              background: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              padding: "0.45rem 0.9rem",
              fontSize: "0.8rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
              boxShadow: "0 1px 4px rgba(220, 53, 69, 0.2)",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#c82333";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "#dc3545";
              e.target.style.transform = "translateY(0)";
            }}
          >
            <FaPlus size={14} />
            Create New Group
          </button>
        </div>

        {/* Search Section */}
        <div className="groups-search-section">
          <div className="groups-search-input-container">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                color: "#666",
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            >
            </div>
            <div className="groups-search-input-wrapper" style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search groups by name, description, or members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="groups-search-input"
                style={{
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  fontSize: "0.9rem",
                  width: "500px",
                  outline: "none",
                  transition: "all 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#dc3545";
                  e.target.style.boxShadow = "0 0 0 3px rgba(220, 53, 69, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e0e0e0";
                  e.target.style.boxShadow = "none";
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    padding: "4px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f0f0f0";
                    e.target.style.color = "#666";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "none";
                    e.target.style.color = "#999";
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {(searchQuery || groups.length !== filteredGroups.length) && (
            <div
              style={{
                fontSize: "0.8rem",
                color: "#666",
                fontWeight: 500,
                padding: "6px 12px",
                background: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef",
              }}
            >
              Showing {filteredGroups.length} of {groups.length} groups
              {searchQuery && ` matching "${searchQuery}"`}
            </div>
          )}
        </div>

        {/* Table Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #e9ecef",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.9rem",
            }}
          >
            <thead
              style={{
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderBottom: "2px solid #dee2e6",
              }}
            >
              <tr>
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: 700,
                    color: "#495057",
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Group Name
                </th>
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: 700,
                    color: "#495057",
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Description
                </th>
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: 700,
                    color: "#495057",
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Members
                </th>
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#495057",
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isGroupLoading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "24px" }}>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                      <div className="table-loader" />
                      <span>Loading Groups...</span>
                    </div>
                  </td>
                </tr>
              ) :
                filteredGroups.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "3rem 2rem",
                        color: "#6c757d",
                        fontSize: "1rem",
                        background: "#f8f9fa",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "1rem",
                        }}
                      >
                        <FaUsers size={48} style={{ opacity: 0.3 }} />
                        <div>
                          {searchQuery
                            ? "No groups found matching your search"
                            : "No groups available"}
                        </div>
                        {!searchQuery && (
                          <button
                            onClick={handleOpenCreate}
                            style={{
                              background: "#dc3545",
                              color: "#fff",
                              border: "none",
                              borderRadius: "5px",
                              padding: "0.45rem 0.9rem",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <FaPlus size={12} />
                            Create Your First Group
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredGroups.map((group, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #f1f3f4",
                        transition: "background 0.2s",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = "#f8f9fa";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                    >
                      <td
                        style={{
                          padding: "16px 20px",
                          fontWeight: 600,
                          color: "#212529",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: "#dc3545",
                              flexShrink: 0,
                            }}
                          ></div>
                          {group.groupName}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#6c757d",
                          maxWidth: "300px",
                        }}
                      >
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {group.description || "No description"}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#495057",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.25rem",
                          }}
                        >
                          {Array.isArray(group.memberLabels) ? (
                            group.memberLabels
                              .slice(0, 2)
                              .map((member, memberIdx) => (
                                <div
                                  key={memberIdx}
                                  style={{
                                    fontSize: "0.8rem",
                                    padding: "2px 6px",
                                    background: "#e9ecef",
                                    borderRadius: "4px",
                                    display: "inline-block",
                                    maxWidth: "200px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {member}
                                </div>
                              ))
                          ) : (
                            <span
                              style={{ color: "#6c757d", fontStyle: "italic" }}
                            >
                              No members
                            </span>
                          )}
                          {Array.isArray(group.memberLabels) &&
                            group.memberLabels.length > 2 && (
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#6c757d",
                                  fontStyle: "italic",
                                }}
                              >
                                +{group.memberLabels.length - 2} more members
                              </div>
                            )}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() => handleEdit(idx)}
                            style={{
                              padding: "0.45rem 0.9rem",
                              borderRadius: "5px",
                              border: "1px solid #dee2e6",
                              background: "#fff",
                              color: "#495057",
                              cursor: "pointer",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = "#f8f9fa";
                              e.target.style.borderColor = "#adb5bd";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = "#fff";
                              e.target.style.borderColor = "#dee2e6";
                            }}
                          >
                            <FaEdit size={12} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(idx)}
                            style={{
                              padding: "0.45rem 0.9rem",
                              borderRadius: "5px",
                              border: "1px solid #dc3545",
                              background: "#fff",
                              color: "#dc3545",
                              cursor: "pointer",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = "#dc3545";
                              e.target.style.color = "#fff";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = "#fff";
                              e.target.style.color = "#dc3545";
                            }}
                          >
                            <FaTrash size={12} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        </div>

        {/* Footer Section */}
        <div
          style={{
            marginTop: "1rem",
            color: "#6c757d",
            fontSize: "0.85rem",
            background: "#fff",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            {filteredGroups.length === 0
              ? "Showing 0 to 0 of 0 entries"
              : `Showing 1 to ${filteredGroups.length} of ${groups.length} entries`}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#495057",
              fontWeight: 500,
            }}
          >
            <FaUsers size={14} />
            Total Groups: {groups.length}
          </div>
        </div>
      </div>
    </DashboardLayout >
  );
};

export default Groups;
